/**
 * CommunicationsWidget — "📧 Comunicazioni" card for the Panoramica tab.
 *
 * Shows the LATEST delivery status of the quote (sent_method, delivery
 * lifecycle event) plus an expandable timeline of the full history.
 *
 * Data model (mirrors backend `services/communications.py`):
 *   - client.quote.{sent_method, delivery_status, sent_at, opened_at,
 *                   clicked_at, bounced_at, complained_at, error,
 *                   message_id, sent_to}
 *   - GET /api/clients/:id/communications → { items: [{kind, channel,
 *                   at, doc_kinds, meta, to}] }
 *
 * Hidden when the quote has never been sent (no sent_method AND no
 * delivery_status). Keeps the Panoramica uncluttered for fresh leads.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Mail, MessageCircle, Link2, Send, CheckCircle2, Eye, MousePointerClick,
  AlertTriangle, XOctagon, Clock, ChevronDown, ChevronUp, RefreshCw, Pencil,
} from "lucide-react";
import api from "@/lib/api";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
};

const CHANNEL_META = {
  email:    { icon: Mail,          label: "Email" },
  whatsapp: { icon: MessageCircle, label: "WhatsApp" },
  link:     { icon: Link2,         label: "Link" },
  system:   { icon: Link2,         label: "Condivisione" },
};

const STATUS_META = {
  sent:       { icon: Send,             label: "Inviata",                  cls: "text-neutral-700",  dot: "bg-neutral-400" },
  delivered:  { icon: CheckCircle2,     label: "Consegnata",               cls: "text-emerald-700", dot: "bg-emerald-500" },
  opened:     { icon: Eye,              label: "Aperta",                   cls: "text-amber-700",   dot: "bg-amber-500" },
  clicked:    { icon: MousePointerClick,label: "Link cliccato",            cls: "text-amber-800",   dot: "bg-amber-600" },
  bounced:    { icon: XOctagon,         label: "Email non recapitata",     cls: "text-rose-700",    dot: "bg-rose-500" },
  complained: { icon: AlertTriangle,    label: "Segnalata come spam",      cls: "text-rose-700",    dot: "bg-rose-500" },
  error:      { icon: AlertTriangle,    label: "Errore di invio",          cls: "text-rose-700",    dot: "bg-rose-500" },
};

const KIND_TO_STATUS = {
  sent: "sent", delivered: "delivered", opened: "opened",
  clicked: "clicked", bounced: "bounced", complained: "complained", error: "error",
};

function StatusBadge({ status }) {
  const m = STATUS_META[status];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium ${m.cls}`}
      data-testid={`comm-status-${status}`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
      {m.label}
    </span>
  );
}

export default function CommunicationsWidget({ client, onNavigate, onResend }) {
  const quote = client?.quote || {};
  const contract = client?.contract || {};
  // The widget surfaces the freshest tracked doc — quote is the primary
  // signal, but we fall back to contract when the operator only sent the
  // contract (rarer, but the data shape is identical).
  const tracked = useMemo(() => {
    const q = quote.delivery_status || quote.sent_method ? { kind: "quote", doc: quote } : null;
    const c = contract.delivery_status || contract.sent_method ? { kind: "contract", doc: contract } : null;
    if (q && c) {
      const qTs = q.doc.last_event_at || q.doc.sent_at || "";
      const cTs = c.doc.last_event_at || c.doc.sent_at || "";
      return qTs >= cTs ? q : c;
    }
    return q || c || null;
  }, [quote, contract]);

  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Lazily fetch the full timeline only when the user expands the card —
  // saves a roundtrip for the majority of Panoramica visits that just
  // glance at the latest status.
  useEffect(() => {
    if (!expanded || !client?.id) return;
    let alive = true;
    setLoadingHistory(true);
    api.get(`/clients/${client.id}/communications`)
      .then(({ data }) => { if (alive) setHistory(data?.items || []); })
      .catch(() => { if (alive) setHistory([]); })
      .finally(() => { if (alive) setLoadingHistory(false); });
    return () => { alive = false; };
  }, [expanded, client?.id]);

  if (!tracked) return null;

  const sub = tracked.doc;
  const channel = sub.sent_method || "email";
  const channelMeta = CHANNEL_META[channel] || CHANNEL_META.email;
  const ChannelIcon = channelMeta.icon;
  const status = sub.delivery_status || "sent";
  const isBounced = status === "bounced" || status === "complained" || status === "error";
  const docLabel = tracked.kind === "contract" ? "Contratto" : "Preventivo";

  // Timeline rows surfaced inline (collapsed = always-visible summary,
  // expanded = full history from the API).
  const summaryRows = [];
  if (sub.sent_at)       summaryRows.push({ status: "sent",       at: sub.sent_at });
  if (sub.delivered_at)  summaryRows.push({ status: "delivered",  at: sub.delivered_at });
  if (sub.opened_at)     summaryRows.push({ status: "opened",     at: sub.opened_at });
  if (sub.clicked_at)    summaryRows.push({ status: "clicked",    at: sub.clicked_at });
  if (sub.bounced_at)    summaryRows.push({ status: "bounced",    at: sub.bounced_at, meta: sub.error });
  if (sub.complained_at) summaryRows.push({ status: "complained", at: sub.complained_at });

  return (
    <Card className="rounded-sm bg-white border-border" data-testid="communications-widget">
      <div className="p-5 border-b border-border flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-primary/10 text-primary">
            <Mail className="w-4 h-4" strokeWidth={1.8} />
          </span>
          <div>
            <h3 className="font-serif-heading text-xl text-neutral-900">Comunicazioni</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Tracciamento invii e interazioni</p>
          </div>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* ------- Latest event summary ------- */}
      <div className="p-5 space-y-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-neutral-800">
            <ChannelIcon className="w-4 h-4 text-primary" strokeWidth={1.8} />
            <span>
              {docLabel} inviato via <strong className="font-medium">{channelMeta.label}</strong>
            </span>
          </div>
          {sub.sent_at && (
            <div className="text-xs text-neutral-500 mt-1 ml-6" data-testid="comm-sent-at">
              {fmtDateTime(sub.sent_at)}
            </div>
          )}
        </div>

        {/* ------- Inline lifecycle (always visible if any event tracked) ------- */}
        {summaryRows.length > 1 && (
          <ul className="space-y-2 ml-1 border-l-2 border-border pl-4" data-testid="comm-summary-rows">
            {summaryRows.slice(1).map((row, idx) => {
              const m = STATUS_META[row.status] || STATUS_META.sent;
              const Icon = m.icon;
              return (
                <li key={idx} className="flex items-start gap-2 text-sm" data-testid={`comm-row-${row.status}`}>
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${m.cls}`} strokeWidth={1.8} />
                  <div className="leading-snug">
                    <span className={`font-medium ${m.cls}`}>{m.label}</span>
                    <span className="text-neutral-500"> · {fmtDateTime(row.at)}</span>
                    {row.meta && (
                      <div className="text-xs text-rose-600 mt-0.5">{row.meta}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* ------- Recipient + actions ------- */}
        <div className="border-t border-border pt-4 space-y-3">
          {(sub.sent_to || client?.email) && (
            <div className="flex items-center gap-2 text-sm text-neutral-700" data-testid="comm-recipient">
              <span className="text-neutral-400 text-xs uppercase tracking-wider">A</span>
              <span className="break-all">{sub.sent_to || client?.email}</span>
            </div>
          )}

          {isBounced && (
            <div
              className="rounded-sm border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 space-y-1.5"
              data-testid="comm-bounce-alert"
            >
              <div className="flex items-center gap-1.5 font-medium">
                <XOctagon className="w-4 h-4" strokeWidth={1.8} />
                {status === "complained" ? "Segnalata come spam" :
                 status === "error" ? "Errore di invio" : "Email non recapitata"}
              </div>
              <p className="text-xs text-rose-700/90 leading-relaxed">
                {sub.error || "Indirizzo non valido o casella inesistente"}
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {isBounced && (
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm h-9 text-xs"
                onClick={() => onNavigate?.("wedding")}
                data-testid="comm-fix-email-button"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.8} />
                Correggi email
              </Button>
            )}
            <Button
              size="sm"
              variant={isBounced ? "default" : "outline"}
              className="rounded-sm h-9 text-xs"
              onClick={() => onResend?.()}
              data-testid="comm-resend-button"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.8} />
              {isBounced ? "Reinvia preventivo" : "Reinvia"}
            </Button>
          </div>
        </div>

        {/* ------- Expand: full timeline ------- */}
        <div className="border-t border-border pt-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            data-testid="comm-toggle-history"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? "Nascondi cronologia" : "Mostra cronologia completa"}
          </button>

          {expanded && (
            <div className="mt-3" data-testid="comm-history-list">
              {loadingHistory && (
                <div className="text-xs text-neutral-500 flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" /> Caricamento…
                </div>
              )}
              {!loadingHistory && history.length === 0 && (
                <div className="text-xs text-neutral-500">Nessun evento ancora registrato.</div>
              )}
              {!loadingHistory && history.length > 0 && (
                <ul className="space-y-2.5 ml-1 border-l-2 border-border pl-4 max-h-72 overflow-y-auto">
                  {history.map((ev, idx) => {
                    const s = KIND_TO_STATUS[ev.kind] || "sent";
                    const m = STATUS_META[s] || STATUS_META.sent;
                    const Icon = m.icon;
                    const cm = CHANNEL_META[ev.channel] || CHANNEL_META.email;
                    return (
                      <li key={ev.id || idx} className="flex items-start gap-2 text-xs" data-testid={`comm-history-${ev.kind}`}>
                        <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${m.cls}`} strokeWidth={1.8} />
                        <div className="leading-snug">
                          <span className={`font-medium ${m.cls}`}>{m.label}</span>
                          <span className="text-neutral-500"> · {cm.label} · {fmtDateTime(ev.at)}</span>
                          {ev.meta?.reason && (
                            <div className="text-rose-600 mt-0.5">{ev.meta.reason}</div>
                          )}
                          {ev.meta?.link && (
                            <div className="text-neutral-500 mt-0.5 break-all">{ev.meta.link}</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
